import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ESignatureDialog from "@/components/ESignatureDialog";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardCheck, ShieldCheck, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MISSING_REASONS = ["Not Collected", "Not Applicable", "Unknown"];

export default function DataEntry() {
  const [studies, setStudies] = useState([]);
  const [studyId, setStudyId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [visits, setVisits] = useState([]);
  const [visitId, setVisitId] = useState("");
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState("");
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [visible, setVisible] = useState({});
  const [esignOpen, setEsignOpen] = useState(false);
  const [pendingSig, setPendingSig] = useState(null);
  const [newSubjectId, setNewSubjectId] = useState("");
  const [newVisit, setNewVisit] = useState({ name: "", date: "" });

  useEffect(() => { (async () => setStudies((await api.get("/studies")).data))(); }, []);
  useEffect(() => {
    if (!studyId) return;
    (async () => {
      setSubjects((await api.get(`/subjects?study_id=${studyId}`)).data);
      setForms((await api.get(`/ecrf/forms?study_id=${studyId}`)).data);
    })();
  }, [studyId]);
  useEffect(() => {
    if (!studyId || !subjectId) return setVisits([]);
    (async () => setVisits((await api.get(`/visits?study_id=${studyId}&subject_id=${subjectId}`)).data))();
  }, [studyId, subjectId]);
  useEffect(() => {
    if (!formId) return setForm(null);
    (async () => {
      const { data } = await api.get(`/ecrf/forms/${formId}`);
      setForm(data);
      const init = {}; data.fields.forEach((f) => { init[f.field_id] = f.type === "multi_choice" ? [] : ""; });
      setValues(init);
    })();
  }, [formId]);

  // Recompute visibility on values change (Principle 4 — control workflow)
  useEffect(() => {
    if (!form) return;
    const vis = {}; form.fields.forEach((f) => { vis[f.field_id] = true; });
    (form.skip_rules || []).forEach((r) => {
      const v = values[r.when_field];
      let hit = false;
      if (r.op === "is_missing") hit = v == null || v === "" || (typeof v === "object" && v?.__missing__);
      else if (v == null) hit = false;
      else if (r.op === "==") hit = v === r.value;
      else if (r.op === "!=") hit = v !== r.value;
      else if (r.op === ">") hit = Number(v) > Number(r.value);
      else if (r.op === ">=") hit = Number(v) >= Number(r.value);
      else if (r.op === "<") hit = Number(v) < Number(r.value);
      else if (r.op === "<=") hit = Number(v) <= Number(r.value);
      else if (r.op === "in") hit = (r.value || []).includes(v);
      else if (r.op === "not_in") hit = !(r.value || []).includes(v);
      if (hit) (r.target_fields || []).forEach((t) => {
        if (r.action === "show") vis[t] = true;
        if (r.action === "hide") vis[t] = false;
      });
    });
    setVisible(vis);
  }, [values, form]);

  const enroll = async () => {
    if (!newSubjectId.trim()) return toast.error("Subject ID required");
    await api.post("/subjects", { study_id: studyId, subject_id: newSubjectId });
    toast.success("Subject enrolled");
    setSubjects((await api.get(`/subjects?study_id=${studyId}`)).data);
    setSubjectId(newSubjectId); setNewSubjectId("");
  };
  const createVisit = async () => {
    if (!newVisit.name || !newVisit.date) return toast.error("Visit name + date");
    await api.post("/visits", { study_id: studyId, subject_id: subjectId, visit_name: newVisit.name, visit_date: newVisit.date });
    setVisits((await api.get(`/visits?study_id=${studyId}&subject_id=${subjectId}`)).data);
    setNewVisit({ name: "", date: "" }); toast.success("Visit added");
  };

  const submit = (withSignature) => {
    if (!formId || !subjectId || !visitId) return toast.error("Study / subject / visit / form required");
    if (withSignature) { setPendingSig({}); setEsignOpen(true); return; }
    doSubmit(null);
  };
  const doSubmit = async (sigId) => {
    try {
      const { data } = await api.post("/ecrf/entries", {
        form_id: formId, subject_id: subjectId, visit_id: visitId, values, signature_id: sigId,
      });
      if (data.state === "Queried") {
        toast.warning(`Entry saved as Queried — ${data.discrepancies.length} discrepancy(ies) opened`);
      } else {
        toast.success(`Entry ${data.state}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };
  const onSigned = (sig) => doSubmit(sig.signature_id);

  const setVal = (fid, v) => setValues({ ...values, [fid]: v });
  const setMissing = (fid, reason) => setValues({ ...values, [fid]: { __missing__: reason } });

  // Recompute the visible field list only when form structure or visibility
  // rules change — avoids re-filtering on every keystroke inside a field.
  const visibleFields = useMemo(
    () => (form?.fields || []).filter((f) => visible[f.field_id] !== false),
    [form, visible],
  );

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="vq-overline mb-2">Clinical / eCRF</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">Data Entry</h1>
        <p className="text-slate-600 mb-6 max-w-3xl">Subject → visit → form. Skip logic hides irrelevant fields live. Out-of-range values auto-open queries.</p>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6">
          <div><Label className="text-xs font-semibold">Study</Label>
            <Select value={studyId} onValueChange={setStudyId}><SelectTrigger className="mt-1.5" data-testid="de-study"><SelectValue placeholder="…" /></SelectTrigger>
              <SelectContent>{studies.map((s) => <SelectItem key={s.study_id} value={s.study_id}>{s.protocol_id}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs font-semibold">Subject</Label>
            <Select value={subjectId} onValueChange={setSubjectId} disabled={!studyId}>
              <SelectTrigger className="mt-1.5" data-testid="de-subject"><SelectValue placeholder="…" /></SelectTrigger>
              <SelectContent>{subjects.map((s) => <SelectItem key={s.subject_id} value={s.subject_id}>{s.subject_id}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-1 mt-1">
              <Input value={newSubjectId} onChange={(e) => setNewSubjectId(e.target.value)} placeholder="+ enroll" className="h-8 text-xs" data-testid="de-enroll-input" />
              <Button size="sm" onClick={enroll} disabled={!studyId} data-testid="de-enroll-btn" className="h-8 bg-slate-900 text-white">+</Button>
            </div>
          </div>
          <div><Label className="text-xs font-semibold">Visit</Label>
            <Select value={visitId} onValueChange={setVisitId} disabled={!subjectId}>
              <SelectTrigger className="mt-1.5" data-testid="de-visit"><SelectValue placeholder="…" /></SelectTrigger>
              <SelectContent>{visits.map((v) => <SelectItem key={v.visit_id} value={v.visit_id}>{v.visit_name} · {v.visit_date}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-1 mt-1">
              <Input value={newVisit.name} onChange={(e) => setNewVisit({ ...newVisit, name: e.target.value })} placeholder="Baseline" className="h-8 text-xs" data-testid="de-visit-name" />
              <Input type="date" value={newVisit.date} onChange={(e) => setNewVisit({ ...newVisit, date: e.target.value })} className="h-8 text-xs" data-testid="de-visit-date" />
              <Button size="sm" onClick={createVisit} disabled={!subjectId} data-testid="de-visit-btn" className="h-8 bg-slate-900 text-white">+</Button>
            </div>
          </div>
          <div><Label className="text-xs font-semibold">Form</Label>
            <Select value={formId} onValueChange={setFormId} disabled={!studyId}>
              <SelectTrigger className="mt-1.5" data-testid="de-form"><SelectValue placeholder="…" /></SelectTrigger>
              <SelectContent>{forms.map((f) => <SelectItem key={f.form_id} value={f.form_id}>{f.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>

        {form && subjectId && visitId ? (
          <div className="vq-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardCheck className="w-5 h-5 text-[#0055FF]" />
              <div className="font-display font-bold text-lg text-slate-900">{form.name}</div>
              <span className="text-[10px] font-mono text-slate-400 ml-2">v{form.version}</span>
            </div>
            <div className="space-y-4">
              {visibleFields.map((f) => (
                <FieldRenderer key={f.field_id} field={f} value={values[f.field_id]} setValue={(v) => setVal(f.field_id, v)} setMissing={(r) => setMissing(f.field_id, r)} />
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={() => submit(false)} data-testid="de-save-btn" className="border-slate-900 text-slate-900">
                <Send className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button onClick={() => submit(true)} data-testid="de-sign-btn" className="bg-slate-900 hover:bg-[#0055FF] text-white">
                <ShieldCheck className="w-4 h-4 mr-2" /> Sign &amp; Submit
              </Button>
              <span className="text-[10px] font-mono text-slate-400">Missing / out-of-range values will open queries automatically.</span>
            </div>
          </div>
        ) : (
          <div className="vq-card p-10 text-center text-slate-500">Select study, subject, visit, and form.</div>
        )}
      </div>
      {pendingSig && (
        <ESignatureDialog open={esignOpen} onOpenChange={setEsignOpen}
                          entityType="ecrf_entry" entityId={`${subjectId}_${visitId}_${formId}`}
                          action="ecrf_entry_submit" defaultMeaning="Author" title="Sign & submit form entry"
                          onSigned={onSigned} />
      )}
    </DashboardLayout>
  );
}

function FieldRenderer({ field, value, setValue, setMissing }) {
  const isMissing = typeof value === "object" && value?.__missing__;
  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
        <Label className="text-sm font-semibold text-slate-900">
          {field.label}
          {field.mandatory && <span className="text-red-600 ml-1">*</span>}
          {field.constraints?.unit && <span className="text-xs font-mono text-slate-400 ml-2">({field.constraints.unit})</span>}
        </Label>
        {field.allow_missing && (
          <Select value={isMissing ? value.__missing__ : ""} onValueChange={(r) => r ? setMissing(r) : setValue("")}>
            <SelectTrigger className="w-40 h-7 text-xs" data-testid={`de-miss-${field.field_id}`}><SelectValue placeholder="Mark missing…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__unset__" disabled>Reason…</SelectItem>
              {MISSING_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {field.help_text && <div className="text-[10px] text-slate-500 mb-2">{field.help_text}</div>}
      {isMissing ? (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-center gap-2">
          <AlertTriangle className="w-3 h-3" /> Marked missing — reason: {value.__missing__}
        </div>
      ) : field.type === "boolean" ? (
        <div className="flex gap-2">
          <Button size="sm" variant={value === true ? "default" : "outline"} onClick={() => setValue(true)} data-testid={`de-yes-${field.field_id}`}
                  className={value === true ? "bg-emerald-600 text-white" : ""}>Yes</Button>
          <Button size="sm" variant={value === false ? "default" : "outline"} onClick={() => setValue(false)} data-testid={`de-no-${field.field_id}`}
                  className={value === false ? "bg-red-600 text-white" : ""}>No</Button>
        </div>
      ) : field.type === "single_choice" ? (
        <Select value={value ?? ""} onValueChange={setValue}>
          <SelectTrigger data-testid={`de-single-${field.field_id}`}><SelectValue placeholder="Choose…" /></SelectTrigger>
          <SelectContent>{(field.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : field.type === "multi_choice" ? (
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((o) => (
            <label key={o} className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded px-2 py-1 cursor-pointer">
              <Checkbox checked={(value || []).includes(o)}
                        onCheckedChange={(v) => setValue(v ? [...(value || []), o] : (value || []).filter((x) => x !== o))} />
              <span>{o}</span>
            </label>
          ))}
        </div>
      ) : field.type === "number" ? (
        <Input type="number" value={value ?? ""} data-testid={`de-num-${field.field_id}`}
               step={field.constraints?.integer ? 1 : field.constraints?.decimals ? Math.pow(10, -field.constraints.decimals) : "any"}
               min={field.constraints?.min} max={field.constraints?.max}
               onChange={(e) => setValue(e.target.value === "" ? "" : Number(e.target.value))} />
      ) : field.type === "date" ? (
        <Input type="date" value={value ?? ""} onChange={(e) => setValue(e.target.value)} data-testid={`de-date-${field.field_id}`} />
      ) : field.type === "short_text" ? (
        <Input value={value ?? ""} onChange={(e) => setValue(e.target.value)} data-testid={`de-short-${field.field_id}`} />
      ) : (
        <Textarea value={value ?? ""} rows={3} onChange={(e) => setValue(e.target.value)} data-testid={`de-long-${field.field_id}`} />
      )}
    </div>
  );
}
